/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import React from 'react';
import { MultiSelect as MantineMultiSelect, ComboboxItem, Menu } from '@mantine/core';
import type { MultiSelectProps as MantineMultiSelectProps } from '@mantine/core';
import type { JSX } from 'preact';
import Input from '@/client/components/legacy/Input';

// Core
import { useMantineInput, InputBaseProps } from '@/client/components/legacy/utils';
import Button, { Props as ButtonProps } from '@/client/components/legacy/Button';
import Popover, { Props as PopoverProps } from '@/client/components/legacy/containers/Popover';
import LegacyMantineProvider from '@/client/components/legacy/mantine/Provider';
import Icon from '@/client/components/shared/Icon';

/*----------------------------------
- TYPES
----------------------------------*/

export type Choice = ComboboxItem;
type SelectChoiceValue = ComboboxItem | string;
type SelectValue = SelectChoiceValue | SelectChoiceValue[] | null | undefined;
type ChoicesLoader = (search: string, current?: string) => Promise<ComboboxItem[]>;
type SharedProps = Omit<MantineMultiSelectProps, 'data' | 'defaultValue' | 'onChange' | 'value'> &
    Omit<InputBaseProps<SelectValue>, 'onChange' | 'value'> & {
        choices?: ComboboxItem[] | ChoicesLoader;
        enableSearch?: boolean;
        dropdown?: { width?: string | number };
        popoverProps?: PopoverProps;
        buttonProps?: ButtonProps;
        addNew?: () => void;
    };
type SingleSelectProps = SharedProps & {
    multiple?: false;
    value?: SelectChoiceValue | null;
    onChange: (value: ComboboxItem | null | undefined) => void;
};
type MultipleSelectProps = SharedProps & {
    multiple: true;
    value?: SelectChoiceValue[] | null;
    onChange: (value: ComboboxItem[]) => void;
};
type RuntimeProps = Omit<MantineMultiSelectProps, 'data' | 'defaultValue' | 'onChange' | 'value'> &
    InputBaseProps<SelectValue> & {
        choices?: ComboboxItem[] | ChoicesLoader;
        enableSearch?: boolean;
        dropdown?: { width?: string | number };
        popoverProps?: PopoverProps;
        buttonProps?: ButtonProps;
        addNew?: () => void;
        multiple?: boolean;
    };
export type Props = SingleSelectProps | MultipleSelectProps;

const ensureChoice = (
    choice: ComboboxItem | string,
    choices: ComboboxItem[],
    current: ComboboxItem[],
): ComboboxItem => {
    // Allready a choice
    if (typeof choice === 'object') return choice;

    // Ensure current is an array of choices
    const allChoices = [...choices, ...current];

    // Find the choice
    const found = allChoices.find((c) => c.value === choice);
    if (found) return found;

    // Create a new choice
    return { label: choice, value: choice };
};

/*----------------------------------
- COMONENT
----------------------------------*/
// Adapt the old component to render with mantine text input
// Don't use useInput hook because it's not mantine compatible
function Select(initProps: SingleSelectProps): JSX.Element;
function Select(initProps: MultipleSelectProps): JSX.Element;
function Select(initProps: RuntimeProps) {
    let [
        { icon, iconR, minimal, onChange, value: current, required },
        { multiple, choices: initChoices, enableSearch, dropdown, popoverProps, buttonProps, addNew, ...props },
    ] = useMantineInput<RuntimeProps, SelectValue>(initProps);

    const currentArray = (Array.isArray(current) ? current : current ? [current] : []).map((c) =>
        ensureChoice(c, [], []),
    );
    const selectedValues = currentArray.map((choice) => choice.value);

    const choicesLoader = typeof initChoices === 'function' ? initChoices : undefined;
    const choicesViaFunc = choicesLoader !== undefined;
    const staticChoices = Array.isArray(initChoices) ? initChoices.map((choice) => ensureChoice(choice, [], currentArray)) : [];
    if (choicesViaFunc) enableSearch = true;

    let [choices, setChoices] = React.useState<ComboboxItem[]>(
        choicesViaFunc ? currentArray.map((choice) => ensureChoice(choice, [], currentArray)) : staticChoices,
    );

    const [opened, setOpened] = React.useState(false);
    const [search, setSearch] = React.useState<{ keywords: string; loading: boolean }>({
        keywords: '',
        loading: !!choicesViaFunc,
    });

    /*----------------------------------
    - ACTIONS
    ----------------------------------*/

    // Load search results
    React.useEffect(() => {
        if (choicesLoader && opened) {
            const currentLabel = !Array.isArray(current) && typeof current === 'object' && current !== null ? current.label : undefined;
            const keywords = search.keywords === currentLabel ? '' : search.keywords;

            //setSearch(s => ({ ...s, loading: true }));
            choicesLoader(keywords).then((searchResults) => {
                //setSearch(s => ({ ...s, loading: false }))
                setChoices(searchResults);
            });
        }
    }, [opened, search.keywords]);

    // When initChoices is not a function and has changed
    React.useEffect(() => {
        if (!choicesViaFunc) {
            setChoices(staticChoices);
        }
    }, [initChoices]);

    /*----------------------------------
    - RENDER
    ----------------------------------*/

    if (minimal) {
        const buttonPrefix =
            multiple && currentArray.length ? (
                <span class="badge bg info s">{currentArray.length}</span>
            ) : icon ? (
                <Icon name={icon} />
            ) : undefined;
        const resolvedButtonProps = buttonPrefix === undefined ? buttonProps : { ...buttonProps, icon: buttonPrefix };
        const resolvedPopoverProps = popoverProps ?? { mode: 'remove' };

        return (
            <Popover
                {...resolvedPopoverProps}
                state={[opened, setOpened]}
                content={
                    <div class="card col menu floating">
                        {enableSearch && (
                            <>
                                <Input
                                    title="Search"
                                    value={search.keywords}
                                    wrapper={false}
                                    minimal
                                    icon="search"
                                    onChange={(v) => setSearch((s) => ({ ...s, keywords: v }))}
                                />
                            </>
                        )}

                        {choices.map((choice) => {
                            const isSelected =
                                current === undefined
                                    ? false
                                    : multiple
                                      ? selectedValues.includes(choice.value)
                                      : selectedValues[0] === choice.value;

                            return (
                                <Button
                                    key={choice.value}
                                    size="s"
                                    suffix={isSelected ? <Icon name="check" /> : undefined}
                                    onClick={() => {
                                        onChange(
                                            multiple
                                                ? isSelected
                                                    ? currentArray.filter((c) => c.value !== choice.value)
                                                    : [...currentArray, choice]
                                                : isSelected && !required
                                                  ? null
                                                  : choice,
                                        );

                                        if (!multiple) setOpened(false);
                                    }}
                                >
                                    {choice.label}
                                </Button>
                            );
                        })}
                    </div>
                }
            >
                <Button
                    {...resolvedButtonProps}
                    suffix={iconR ? <Icon name={iconR} /> : <Icon name="angle-down" />}
                    onClick={() => setOpened((o) => !o)}
                >
                    {props.label || props.placeholder}
                </Button>
            </Popover>
        );
    } else {
        return (
            <LegacyMantineProvider>
                <MantineMultiSelect
                    {...props}
                    value={selectedValues}
                    onChange={(value: string[]) => {
                        if (multiple) onChange(value.map((entry) => ensureChoice(entry, choices, currentArray)));
                        else {
                            setOpened(false);
                            onChange(value.length > 0 ? ensureChoice(value[value.length - 1], choices, currentArray) : undefined);
                        }
                    }}
                    data={[
                        {
                            group: 'Search results',
                            // Exclude the choices that are already selected
                            items: choices.filter((c) => !selectedValues.includes(c.value)),
                        },
                        { group: 'Selected', items: currentArray },
                    ]}
                    nothingFoundMessage={search.loading ? 'Loading...' : 'No options found'}
                    comboboxProps={{ withArrow: false, width: dropdown?.width }}
                    searchable={enableSearch}
                    clearable={!required}
                    required={required}
                    checkIconPosition="right"
                    dropdownOpened={opened}
                    onDropdownOpen={() => setOpened(true)}
                    onDropdownClose={() => setOpened(false)}
                    onSearchChange={(keywords) => setSearch((s) => ({ ...s, keywords }))}
                />
            </LegacyMantineProvider>
        );
    }
}

export default Select;
